import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AnchorHTMLAttributes } from 'react';
import type { Plugin } from 'unified';
import type { Root, Text, Link, PhrasingContent, Parent } from 'mdast';
import { visit, SKIP } from 'unist-util-visit';
import { shortcodeToEmoji } from '@/lib/emoji-shortcodes';

interface Props {
  content: string;
  /** Current user's username — used to highlight self-mentions. */
  currentUsername?: string;
}

function safeHref(href: string | undefined): string | undefined {
  if (!href) return undefined;
  if (/^(https?:|mailto:|tel:)/i.test(href)) return href;
  return undefined;
}

// Username pattern mirrors the backend rule: 1–32 chars of letters, digits,
// dot, dash, underscore. We anchor on a non-word, non-`@` boundary so
// `foo@example.com` and `user@host` (emails) are NOT picked up.
const MENTION_RE = /(^|[^\w@])@([a-zA-Z0-9._-]{1,32})/g;

/**
 * Remark plugin: convert `@username` text fragments into mdast `link`
 * nodes whose URL is `mention:username`. This puts the chip in the
 * downstream `a` component override, where we style it as a span.
 *
 * Why a plugin instead of string preprocessing: react-markdown's link
 * parser only matches `[text](url)` when the URL is a recognized scheme
 * (http, mailto, etc). Bare strings like `[hi @alice](mention:alice)`
 * fall through as literal text. Doing the transform in the AST means
 * the link node reaches our custom renderer with the right href.
 */
const remarkMentions: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree, 'text', (node: Text, index, parent) => {
      if (!parent || typeof index !== 'number') return;
      // Don't touch inside code/inlineCode — users may legitimately
      // write `@something` inside code samples without it being a
      // mention.
      if ((parent as Parent).type === 'inlineCode') return;

      const value = node.value;
      if (!value.includes('@')) return;
      MENTION_RE.lastIndex = 0;
      const matches = [...value.matchAll(MENTION_RE)];
      if (matches.length === 0) return;

      const newNodes: PhrasingContent[] = [];
      let cursor = 0;
      for (const m of matches) {
        const lead = m[1] ?? '';
        const name = m[2] ?? '';
        const start = (m.index ?? 0) + lead.length;
        if (start > cursor) {
          newNodes.push({ type: 'text', value: value.slice(cursor, start) } as Text);
        }
        const linkNode: Link = {
          type: 'link',
          url: `mention:${name}`,
          title: null,
          children: [{ type: 'text', value: `@${name}` } as Text],
        };
        newNodes.push(linkNode);
        cursor = start + 1 + name.length; // +1 for the '@'
      }
      if (cursor < value.length) {
        newNodes.push({ type: 'text', value: value.slice(cursor) } as Text);
      }
      (parent as Parent).children.splice(index, 1, ...newNodes);
      return [SKIP, cursor];
    });
  };
};

export default function Markdown({ content, currentUsername }: Props) {
  // F-012: `:smile:` → 😀. Conversion happens at render time so the
  // composer can keep showing the raw shortcode while the user types —
  // they see what they're writing, and the renderer turns it into the
  // unicode glyph only when displaying. Unknown shortcodes pass through
  // unchanged. Mentions are handled by the remark plugin below.
  const rendered = shortcodeToEmoji(content);

  return (
    <div className="md-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMentions]}
        // react-markdown@9 sanitizes URLs by default; `mention:foo` isn't
        // a recognized safe scheme so we whitelist it here so the chip
        // renderer fires.
        urlTransform={(url) => (url.startsWith('mention:') ? url : safeHref(url) ?? '')}
        skipHtml
        components={{
          a: ({ node: _node, href, children, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { node?: unknown }) => {
            // Internal mention chip — `mention:<username>`. Render as a
            // styled span instead of a link so we don't navigate away
            // when clicked; navigation to the user's profile is handled
            // by the parent Message component (future enhancement).
            if (typeof href === 'string' && href.startsWith('mention:')) {
              const username = href.slice('mention:'.length);
              const isSelf = currentUsername && username.toLowerCase() === currentUsername.toLowerCase();
              return (
                <span
                  className={
                    'mention-chip' +
                    (isSelf ? ' mention-chip--self' : '')
                  }
                  data-mention={username}
                  role="link"
                  tabIndex={0}
                  aria-label={`Mention ${username}`}
                >
                  @{username}
                </span>
              );
            }
            const safe = safeHref(href);
            if (!safe) return <span>{children as React.ReactNode}</span>;
            return (
              <a {...rest} href={safe} target="_blank" rel="noopener noreferrer nofollow">
                {children as React.ReactNode}
              </a>
            );
          },
          img: () => null,
        }}
      >
        {rendered}
      </ReactMarkdown>
    </div>
  );
}
